import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CartService } from '../core/cart/cart.service';
import { formatCartSummary } from '../core/cart/cart';

@Component({
  selector: 'ct-cart-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'cart-bar-host',
  },
  imports: [RouterLink],
  template: `
    @if (cart.hasItems()) {
      <a class="cart-bar" routerLink="/panier" [attr.aria-label]="ariaLabel()">
        <span class="icon" aria-hidden="true">🧺</span>
        <span class="copy">
          @if (cart.isMultiProducer()) {
            <strong>{{ cart.producerCount() }} producteurs</strong>
          } @else {
            <strong>{{ cart.producerName() }}</strong>
          }
          <span>{{ summary() }}</span>
        </span>
        <span class="cta">Voir le panier</span>
      </a>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .cart-bar {
      display: flex;
      align-items: center;
      gap: var(--ct-space-3);
      padding: var(--ct-space-2) var(--ct-space-4);
      background: var(--ct-terre-cuite, #c17f59);
      color: var(--ct-kraft);
      text-decoration: none;
      border-bottom: 1px solid color-mix(in srgb, var(--ct-kraft) 20%, transparent);
      font-size: var(--ct-size-sm);
    }

    .cart-bar:hover,
    .cart-bar:focus-visible {
      background: color-mix(in srgb, var(--ct-terre-cuite, #c17f59) 88%, black);
    }

    .icon {
      font-size: 1.25rem;
      line-height: 1;
    }

    .copy {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      min-width: 0;
      flex: 1;
    }

    .copy strong {
      font-size: var(--ct-size-md);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cta {
      font-weight: var(--ct-weight-bold);
      white-space: nowrap;
    }

    @media (width < 34rem) {
      .cart-bar {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 6;
        border-bottom: 0;
        border-top: 1px solid color-mix(in srgb, var(--ct-kraft) 20%, transparent);
        padding-bottom: calc(var(--ct-space-2) + env(safe-area-inset-bottom, 0px));
      }

      :host(.cart-bar-host--desktop) {
        display: none;
      }
    }

    @media (width >= 34rem) {
      :host(.cart-bar-host--mobile) {
        display: none;
      }
    }
  `,
})
export class CtCartBar {
  protected readonly cart = inject(CartService);

  protected summary(): string {
    return formatCartSummary(
      this.cart.itemCount(),
      this.cart.totalCents(),
      this.cart.producerCount(),
    );
  }

  protected ariaLabel(): string {
    if (this.cart.isMultiProducer()) {
      return `Panier, ${this.summary()}`;
    }
    return `Panier chez ${this.cart.producerName()}, ${this.summary()}`;
  }
}
